import { api } from '../../api/client';
import { recordCheckoutHandoff } from './checkoutHandoff';

export interface GridSlot {
  startUtc: string;
  endUtc: string;
  state: 'Free' | 'BlockedHard' | 'BlockedBuffer';
}

export interface BufferStrip {
  startUtc: string;
  endUtc: string;
}

/**
 * Who holds a booked span — shown to every active user so coaches can see who to talk to about
 * swapping slots. Team label plus the team's active coaches; deliberately no booking id.
 * Unpublished club matchday allocations have no occupancy row at all (their slots stay anonymous).
 */
export interface SlotOccupancy {
  startUtc: string;
  endUtc: string;
  teamAgeGroup: string;
  teamName: string;
  coachNames: string[];
  /**
   * Set when the team is actually playing on a DIFFERENT, physically overlapping pitch (the
   * conflict matrix) — this cell is blocked by that game, and names it rather than showing an
   * unexplained "booked".
   */
  onPitchName: string | null;
}

export interface PitchAvailability {
  pitchId: number;
  name: string;
  surface: string;
  format: string;
  bufferMinutes: number;
  isFloodlit: boolean;
  slots: GridSlot[];
  bufferStrips: BufferStrip[];
  occupancies: SlotOccupancy[];
  /**
   * The club has taken this pitch out of action for the date. Every slot is already blocked —
   * the flag and reason exist so the card can say why instead of showing a column of "booked".
   */
  isClosed: boolean;
  closureReason: string | null;
}

export interface DayAvailability {
  date: string;
  venueId: number;
  venueName: string;
  slotMinutes: number;
  closed: boolean;
  closureReason: string | null;
  pitches: PitchAvailability[];
}

export interface MyTeam {
  id: number;
  name: string;
  ageGroup: string;
  defaultFormat: string;
  /** The age group's configured full-game length — drives the default Match duration. */
  matchMinutes: number;
  /**
   * Whether the CALLER coaches this team. Always true for coaches; for admins it separates
   * their own teams (ordinary coach flow) from on-behalf bookings (admin wording, auto-approve,
   * payment request sent to the team's coach).
   */
  isCoached: boolean;
}

/** One active coach of a team — the payment-request recipient for an on-behalf booking. */
export interface TeamCoachOption {
  userId: number;
  displayName: string;
}

interface AdminTeamRow {
  id: number;
  coaches: { userId: number; displayName: string; isActive: boolean }[];
}

/**
 * Active coaches per team, for the admin's payer picker on /book — mirrors the scheduler's
 * payable-placement picker (inactive coaches are excluded; the server refuses them anyway).
 */
export const fetchTeamCoaches = async (): Promise<Record<number, TeamCoachOption[]>> => {
  const rows = await api<AdminTeamRow[]>('/api/admin/teams');
  return Object.fromEntries(rows.map((t) => [
    t.id,
    t.coaches.filter((c) => c.isActive).map(({ userId, displayName }) => ({ userId, displayName })),
  ]));
};

export interface MyBooking {
  id: number;
  venueName: string;
  pitchName: string;
  teamId: number;
  teamName: string;
  startUtc: string;
  endUtc: string;
  type: string;
  matchType: string | null;
  status: string;
  isFoc: boolean;
  pricePence: number;
  /** Sum of succeeded payments — FOC/paid/unpaid is derived from this plus price and status. */
  paidPence: number;
  refundedPence: number;
  bookedBy: string;
  /** When the booking/request was made — the "Requested" stamp. */
  createdUtc: string;
  /** Set when an admin explicitly approved it (match decisions, payable placements). */
  approvedUtc: string | null;
  approvedBy: string | null;
  holdExpiresUtc: string | null;
  canCancel: boolean;
  canPay: boolean;
  decisionReason: string | null;
}

export interface BookingCreated {
  bookingId: number;
  status: string;
  pricePence: number;
  holdExpiresUtc: string | null;
}

export interface VenueSummary {
  id: number;
  name: string;
}

/** Bookable venues (those with active pitches) — one stacked section each on the booking page. */
export const fetchVenues = () => api<VenueSummary[]>('/api/venues');

/**
 * Only a matchday match is buffered, so the grid depends on the intended booking type: the coach
 * page fetches the Training perspective (its default flow — opens up back-to-back slots). A slot
 * booked as a match request is still fully re-checked server-side at submit.
 */
export const fetchDay = (venueId: number, date: string, type: 'Training' | 'Match' = 'Training') =>
  api<DayAvailability>(`/api/venues/${venueId}/availability?date=${date}&type=${type}`);

export const fetchMyTeams = () => api<MyTeam[]>('/api/teams/mine');

/** Price preview for the slot sheet — same server-side pricing the create path uses. */
export const fetchQuote = (pitchId: number, startUtc: string, endUtc: string) =>
  api<{ pricePence: number | null }>(
    `/api/bookings/quote?pitchId=${pitchId}&startUtc=${encodeURIComponent(startUtc)}&endUtc=${encodeURIComponent(endUtc)}`,
  );

export const fetchMyBookings = () => api<MyBooking[]>('/api/bookings/mine');

export const createBooking = (body: {
  pitchId: number;
  teamId: number;
  startUtc: string;
  endUtc: string;
  type: 'Training' | 'Match';
  matchType: string | null;
  notes: string | null;
  /** Admin-only free-of-charge grant — the server 403s a non-admin sending it. */
  isFoc?: boolean;
  focReason?: string | null;
  /**
   * Admin-only, on-behalf bookings: the active coach who owns the booking and is sent the
   * payment request (instead of the admin paying at checkout). The server 403s a non-admin.
   */
  payerUserId?: number | null;
}) => api<BookingCreated>('/api/bookings', { method: 'POST', body: JSON.stringify(body) });

/**
 * Every coach-facing checkout goes through here, so this is the one place that records the
 * handoff the cancel-URL return is checked against (see checkoutHandoff.ts).
 */
export const createCheckoutSession = async (bookingId: number) => {
  const session = await api<{ url: string }>(
    `/api/bookings/${bookingId}/checkout-session`, { method: 'POST' },
  );
  recordCheckoutHandoff(bookingId);
  return session;
};

/**
 * Asks the server to check with Stripe whether this booking's checkout session has been paid,
 * confirming it on the spot instead of waiting for the webhook. Safe to call on any return from
 * checkout — an unpaid session just answers 'pending' with no side effects. 'needs-resolution'
 * means the money arrived but the coach may NOT hold the slot (paid after a cancel/clash) — an
 * admin is on it, and the UI must not call that booking confirmed.
 */
export type ReconcileStatus = 'confirmed' | 'needs-resolution' | 'pending';

export const reconcilePayment = (bookingId: number) =>
  api<{ status: ReconcileStatus }>(`/api/bookings/${bookingId}/reconcile-payment`, { method: 'POST' });

/**
 * Coach backed out of Stripe: free the slot now rather than leaving it held until the hold
 * lapses. A block-booking week releases its whole series (all weeks share one hold), reported by
 * releasedCount. 409s when the payment actually went through, or when the provider can't confirm
 * that it didn't — in both cases the booking is deliberately left alone.
 */
export const releaseHold = (bookingId: number) =>
  api<{ released: boolean; releasedCount?: number }>(
    `/api/bookings/${bookingId}/release-hold`, { method: 'POST' });

/** One weekly occurrence of a block booking as the preview/apply endpoints report it. */
export interface BlockWeek {
  date: string;
  startUtc: string;
  endUtc: string;
  status: 'Ok' | 'Past' | 'Closed' | 'OutsideHours' | 'HardClash' | 'BufferClash';
  detail: string | null;
  pricePence: number | null;
}

export interface BlockPreview {
  previewFingerprint: string;
  startDate: string;
  dayOfWeek: string;
  maxBlockWeeks: number;
  bookableWeeks: number;
  totalPence: number;
  priceMissing: boolean;
  weeks: BlockWeek[];
}

export interface BlockBookingBody {
  pitchId: number;
  teamId: number;
  startUtc: string;
  endUtc: string;
  weeks: number;
  notes: string | null;
  /** Admin-only free-of-charge grant — the server 403s a non-admin sending it. */
  isFoc?: boolean;
  focReason?: string | null;
  /** Admin-only: the coach who owns the series and owes each week (payment requests, no checkout). */
  payerUserId?: number | null;
}

/**
 * `dueNowPence` is the first week alone — the only one held and payable at checkout. `totalPence`
 * is the whole block; later weeks are billed one at a time from My bookings. `holdExpiresUtc` is
 * null for an admin FOC block — every week confirms at apply time and nothing is held.
 */
export interface BlockApplied {
  allocationId: number;
  bookingIds: number[];
  createdWeeks: BlockWeek[];
  skippedWeeks: BlockWeek[];
  totalPence: number;
  dueNowPence: number;
  holdExpiresUtc: string | null;
}

export interface BlockExtendPreview {
  allocationId: number;
  teamName: string;
  venueName: string;
  pitchName: string;
  currentEndDate: string;
  dayOfWeek: string;
  startTimeLocal: string;
  maxBlockWeeks: number;
  bookableWeeks: number;
  totalPence: number;
  priceMissing: boolean;
  weeks: BlockWeek[];
}

export interface BlockExtended extends BlockApplied {
  newEndDate: string;
}

export const previewBlockExtend = (allocationId: number, weeks: number) =>
  api<BlockExtendPreview>(`/api/bookings/block/${allocationId}/extend/preview`, {
    method: 'POST',
    body: JSON.stringify({ weeks }),
  });

export const applyBlockExtend = (allocationId: number, weeks: number) =>
  api<BlockExtended>(`/api/bookings/block/${allocationId}/extend`, {
    method: 'POST',
    body: JSON.stringify({ weeks }),
  });

export const previewBlockBooking = (body: BlockBookingBody) =>
  api<BlockPreview>('/api/bookings/block/preview', { method: 'POST', body: JSON.stringify(body) });

export const applyBlockBooking = (body: BlockBookingBody & { previewFingerprint: string }) =>
  api<BlockApplied>('/api/bookings/block', { method: 'POST', body: JSON.stringify(body) });

// The series checkout/simulator endpoints are deliberately NOT bound here. Weeks are billed one at
// a time, so every coach-facing payment goes through createCheckoutSession/simulatePayment on a
// single booking id. The server keeps those routes to settle blocks booked before per-week billing
// (their multi-week Stripe sessions are still in flight); nothing in the SPA should call them, and
// binding them again would quietly reintroduce pay-the-whole-block-up-front.

/**
 * Dev/test-only endpoint (does not exist in Production). Callers treat a 404 as
 * "payments genuinely unavailable" and surface an honest message instead of a dev fallback.
 */
export const simulatePayment = (bookingId: number) =>
  api(`/api/bookings/${bookingId}/simulate-payment`, { method: 'POST' });

/** refundReview: the booking was paid — an admin now reviews whether a refund is due. */
export const cancelBooking = (bookingId: number, reason: string | null) =>
  api<{ refundReview: boolean }>(`/api/bookings/${bookingId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const formatPrice = (pence: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);

/** £0 is a deliberate free-of-charge price (e.g. grass pitches) — never rendered as "£0.00". */
export const formatPriceOrFree = (pence: number) =>
  pence === 0 ? 'Free of charge' : formatPrice(pence);
